"use client";

import { useEffect, useRef, useState } from "react";
import AppStoreLinks from "@/features/device/components/AppStoreLinks";
import {
  AppleLogo,
  SpotifyLogo,
  YoutubeLogo,
} from "@/components/ui/icons";
import { cn } from "@/lib/utils";

const PROVIDER_CLICK_EXIT_DELAY_MS = 180;

type ProviderAction = {
  key: "apple" | "youtube" | "spotify";
  label: string;
  Icon: typeof AppleLogo;
  iconClassName?: string;
  onClick: () => void | Promise<void>;
  onPrime?: () => void;
};

type ProviderKey = ProviderAction["key"];

type LoginProviderSectionProps = {
  participantLoginDisabled: boolean;
  participantDisabledHint: string | null;
  showMobileBuilds: boolean;
  checkingSession: boolean;
  onPrimeAppleMusicKit: () => void;
  onAppleMusicLogin: () => void;
  onYouTubeLogin: () => void;
  onSpotifyLogin: () => void;
};

export function LoginProviderSection({
  participantLoginDisabled,
  participantDisabledHint,
  showMobileBuilds,
  checkingSession,
  onPrimeAppleMusicKit,
  onAppleMusicLogin,
  onYouTubeLogin,
  onSpotifyLogin,
}: LoginProviderSectionProps) {
  const launchTimeoutRef = useRef<number | null>(null);
  const launchResetTimeoutRef = useRef<number | null>(null);
  const [launchingProviderKey, setLaunchingProviderKey] =
    useState<ProviderKey | null>(null);

  const providerActions: ProviderAction[] = [
    {
      key: "apple",
      label: "Apple Music",
      Icon: AppleLogo,
      iconClassName: "-translate-y-px",
      onClick: onAppleMusicLogin,
      onPrime: onPrimeAppleMusicKit,
    },
    {
      key: "youtube",
      label: "YouTube",
      Icon: YoutubeLogo,
      onClick: onYouTubeLogin,
    },
    {
      key: "spotify",
      label: "Spotify",
      Icon: SpotifyLogo,
      onClick: onSpotifyLogin,
    },
  ];
  const providerButtonsDisabled =
    participantLoginDisabled || launchingProviderKey !== null;

  useEffect(() => {
    return () => {
      if (
        typeof window !== "undefined" &&
        launchTimeoutRef.current !== null
      ) {
        window.clearTimeout(launchTimeoutRef.current);
      }

      if (
        typeof window !== "undefined" &&
        launchResetTimeoutRef.current !== null
      ) {
        window.clearTimeout(launchResetTimeoutRef.current);
      }
    };
  }, []);

  const handleProviderClick = (provider: ProviderAction) => {
    if (providerButtonsDisabled) return;
    setLaunchingProviderKey(provider.key);

    if (
      typeof window !== "undefined" &&
      launchTimeoutRef.current !== null
    ) {
      window.clearTimeout(launchTimeoutRef.current);
    }

    if (
      typeof window !== "undefined" &&
      launchResetTimeoutRef.current !== null
    ) {
      window.clearTimeout(launchResetTimeoutRef.current);
      launchResetTimeoutRef.current = null;
    }

    launchTimeoutRef.current = window.setTimeout(() => {
      launchTimeoutRef.current = null;
      void Promise.resolve(provider.onClick())
        .catch(() => {
          setLaunchingProviderKey(null);
        })
        .finally(() => {
          if (typeof window === "undefined") return;
          launchResetTimeoutRef.current = window.setTimeout(() => {
            setLaunchingProviderKey((current) =>
              current === provider.key ? null : current,
            );
            launchResetTimeoutRef.current = null;
          }, 900);
        });
    }, PROVIDER_CLICK_EXIT_DELAY_MS);
  };

  return (
    <div className={"mt-[clamp(0.95rem,2vh,1.15rem)] grid gap-[clamp(0.9rem,1.9vh,1.1rem)]"}>
      <div className={"text-center"}>
        <p className={"theme-copy-label text-[0.6875rem] font-bold uppercase tracking-[0.18em]"}>
          Participant sign-in
        </p>

        <div className={"mt-3 flex w-full justify-center"}>
          <div className={"grid w-full max-w-[26rem] grid-cols-3 gap-[0.6rem] sm:max-w-[28rem] sm:gap-[0.8rem]"}>
            {providerActions.map((provider) => (
              <button
                key={provider.key}
                type="button"
                onPointerEnter={provider.onPrime}
                onPointerDown={provider.onPrime}
                onFocus={() => {
                  provider.onPrime?.();
                }}
                onClick={() => {
                  handleProviderClick(provider);
                }}
                disabled={providerButtonsDisabled}
                aria-label={`Continue with ${provider.label}`}
                title={provider.label}
                className={cn(
                  "theme-button-secondary theme-login-control flex min-h-[clamp(5.25rem,11vh,6rem)] w-full min-w-0 flex-col items-center justify-center gap-[0.45rem] rounded-[var(--radius-lg)] border px-[0.55rem] py-[0.75rem] transition-[transform,border-color,background,color,box-shadow] duration-200 enabled:cursor-pointer hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[color:color-mix(in_srgb,var(--color-primary)_26%,transparent)] disabled:cursor-not-allowed disabled:opacity-70 sm:min-h-[clamp(5.75rem,11.5vh,6.6rem)] sm:gap-[0.55rem] sm:px-[0.65rem] sm:py-[0.8rem]",
                  launchingProviderKey === provider.key &&
                    "scale-[0.975]",
                )}
              >
                <provider.Icon
                  className={cn(
                    "theme-login-provider-icon h-6 w-6 shrink-0 sm:h-[1.7rem] sm:w-[1.7rem]",
                    provider.iconClassName,
                  )}
                  aria-hidden="true"
                />
                <span className={"max-w-full text-center text-[0.78rem] font-[650] leading-[1.2] text-[color:color-mix(in_oklch,var(--color-foreground)_84%,transparent)] sm:text-[0.84rem]"}>
                  {provider.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {participantDisabledHint ? (
          <p className={"theme-copy-muted mt-3 text-[0.75rem] leading-[1.5]"}>
            {participantDisabledHint}
          </p>
        ) : null}
      </div>

      {showMobileBuilds ? (
        <div className={"hidden text-center md:block"}>
          <p className={"theme-copy-label text-[0.6875rem] font-bold uppercase tracking-[0.18em]"}>
            Mobile builds
          </p>
          <AppStoreLinks
            className={"mx-auto mt-3 w-full max-w-[16.65rem] sm:max-w-[17.9rem]"}
            variant="provider"
          />
        </div>
      ) : null}

      {checkingSession ? (
        <p className={"text-center text-[0.75rem] text-[color:var(--color-muted-foreground)]"}>
          Checking session...
        </p>
      ) : null}
    </div>
  );
}
